import { fetchAuthSession } from 'aws-amplify/auth';

export type ProgressResourceType = 'story' | 'video' | 'character' | 'voice';

export interface ProgressUpdate {
  resourceType: ProgressResourceType;
  resourceId: string;
  stage: string;
  message: string;
  progress: number;
  sequence?: number;
  metadata?: Record<string, any>;
  timestamp: number;
}

type ProgressCallback = (update: ProgressUpdate) => void;
type CompleteCallback = (data: any) => void;
type ErrorCallback = (error: any) => void;

interface Subscription {
  id: number;
  resourceType: ProgressResourceType;
  resourceId: string;
  requestId?: string;
  onProgress?: ProgressCallback;
  onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
  onComplete?: CompleteCallback;
  onError?: ErrorCallback;
}

class WebSocketManagerV2 {
  private static instance: WebSocketManagerV2 | null = null;
  private ws: WebSocket | null = null;
  private url: string;
  private userId: string | null = null;
  private isAuthenticated = false;
  private isIntentionallyClosed = false;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private subscriptions: Map<string, Map<number, Subscription>> = new Map();
  private lastSequence: Map<string, number> = new Map();
  private messageBuffer: Map<string, ProgressUpdate[]> = new Map();
  private latestProgress: Map<string, ProgressUpdate> = new Map();
  private activeOperationByResource: Map<string, string> = new Map();
  private nextSubscriptionId = 1;
  private onOpenCallbacks: Array<() => void> = [];
  private onCloseCallbacks: Array<() => void> = [];

  private constructor(url: string) {
    this.url = url;
  }

  public static getInstance(url: string): WebSocketManagerV2 {
    if (WebSocketManagerV2.instance && WebSocketManagerV2.instance.url !== url) {
      WebSocketManagerV2.instance.close();
    }

    if (!WebSocketManagerV2.instance) {
      WebSocketManagerV2.instance = new WebSocketManagerV2(url);
    }

    return WebSocketManagerV2.instance;
  }

  public async connect(userId: string): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.userId = userId;
    this.isIntentionallyClosed = false;

    try {
      const authenticatedUrl = await this.getAuthenticatedUrl();
      this.ws = new WebSocket(authenticatedUrl);
      this.ws.onopen = () => this.handleOpen();
      this.ws.onmessage = (event) => this.handleMessage(event);
      this.ws.onerror = (error) => this.handleError(error);
      this.ws.onclose = () => this.handleClose();
    } catch (error: any) {
      console.error('[WSv2] Error connecting:', error);
      if (error?.message !== 'No authentication token available') {
        this.scheduleReconnect();
      }
    }
  }

  private async getAuthenticatedUrl(): Promise<string> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    const separator = this.url.includes('?') ? '&' : '?';
    return `${this.url}${separator}Authorization=${encodeURIComponent(idToken)}`;
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;

    if (this.userId) {
      this.sendMessage({ action: 'authenticate', userId: this.userId });
    }

    this.startHeartbeat();
    this.onOpenCallbacks.forEach((callback) => callback());
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'authenticated':
          this.isAuthenticated = true;
          this.sendMessage({ action: 'startPolling', userId: this.userId });
          this.resubscribeAll();
          if (Array.isArray(data.activeProgress)) {
            data.activeProgress.forEach((progress: any) => {
              this.notifyProgressSnapshot(this.normalizeProgress(progress));
            });
          }
          break;

        case 'subscribed':
          this.handleSubscribed(data);
          break;

        case 'progress':
          {
            const normalized = this.normalizeProgress(data);
            const progressKey = this.prepareProgressStream(normalized);
            const currentLastSequence = this.lastSequence.get(progressKey) || 0;
            if (data.sequence === 1 && currentLastSequence > 0) {
              this.lastSequence.delete(progressKey);
              this.messageBuffer.delete(progressKey);
              this.latestProgress.delete(progressKey);
            }
            this.notifySubscribers(normalized);
          }
          break;

        case 'complete':
          this.handleComplete(data);
          break;

        case 'error':
          this.handleSubscriptionError(data);
          break;

        case 'heartbeat':
          break;

        case 'pollingEnding':
          if (this.userId && this.subscriptions.size > 0) {
            this.sendMessage({ action: 'startPolling', userId: this.userId });
          }
          break;

        default:
          console.warn('[WSv2] Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('[WSv2] Error parsing message:', error);
    }
  }

  private normalizeProgress(data: any): ProgressUpdate {
    return {
      resourceType: (data.resourceType || 'story') as ProgressResourceType,
      resourceId: data.resourceId,
      stage: data.stage,
      message: data.message,
      progress: data.progress,
      sequence: data.sequence,
      metadata: data.metadata,
      timestamp: data.timestamp || Date.now(),
    };
  }

  private getResourceId(data: any): string {
    return data.resourceId || data.storyId || data.videoId || data.characterId || data.voiceId;
  }

  private getResourceType(data: any): ProgressResourceType {
    return (data.resourceType || (data.voiceId ? 'voice' : data.characterId ? 'character' : data.videoId ? 'video' : 'story')) as ProgressResourceType;
  }

  private getSubscriptionsForKey(key: string): Subscription[] {
    return Array.from(this.subscriptions.get(key)?.values() || []);
  }

  private handleSubscribed(data: any): void {
    const resourceType = this.getResourceType(data);
    const resourceId = this.getResourceId(data);
    const key = `${resourceType}:${resourceId}`;
    const subscriptions = this.getSubscriptionsForKey(key);

    if (subscriptions.length === 0) {
      return;
    }

    const initialProgress = data.currentProgress ? this.normalizeProgress(data.currentProgress) : null;
    subscriptions.forEach((subscription) => subscription.onInitialProgress?.(initialProgress));

    if (initialProgress && initialProgress.stage !== 'complete') {
      this.notifyProgressSnapshot(initialProgress);
    }
  }

  private handleComplete(data: any): void {
    const resourceType = this.getResourceType(data);
    const resourceId = this.getResourceId(data);
    const key = `${resourceType}:${resourceId}`;

    this.getSubscriptionsForKey(key).forEach((subscription) => {
      subscription.onComplete?.(data.story || data);
    });

    this.notifyProgressSnapshot({
      resourceType,
      resourceId,
      stage: 'complete',
      message: data.message || 'Story ready!',
      progress: 100,
      sequence: data.sequence || 999999,
      metadata: data.metadata,
      timestamp: Date.now(),
    });

    this.lastSequence.delete(key);
    this.messageBuffer.delete(key);
    this.latestProgress.delete(key);
    this.activeOperationByResource.delete(key);
  }

  private handleSubscriptionError(data: any): void {
    const resourceType = this.getResourceType(data);
    const resourceId = this.getResourceId(data);
    const key = `${resourceType}:${resourceId}`;
    this.getSubscriptionsForKey(key).forEach((subscription) => subscription.onError?.(data.error || data));
    this.lastSequence.delete(key);
    this.messageBuffer.delete(key);
    this.latestProgress.delete(key);
    this.activeOperationByResource.delete(key);
  }

  private handleError(error: Event): void {
    console.error('[WSv2] WebSocket error:', error);
  }

  private handleClose(): void {
    this.isAuthenticated = false;
    this.stopHeartbeat();
    this.onCloseCallbacks.forEach((callback) => callback());

    if (!this.isIntentionallyClosed) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= 10) {
      console.error('[WSv2] Max reconnect attempts reached');
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts += 1;
      if (this.userId) {
        this.connect(this.userId);
      }
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendMessage({ action: 'heartbeat' });
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private sendMessage(message: Record<string, any>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  public subscribeToStory(
    storyId: string,
    callbacks: {
      onProgress?: ProgressCallback;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: CompleteCallback;
      onError?: ErrorCallback;
    },
  ): () => void {
    return this.subscribe('story', storyId, callbacks);
  }

  public subscribeToVideo(
    videoId: string,
    callbacks: {
      onProgress?: ProgressCallback;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: CompleteCallback;
      onError?: ErrorCallback;
    },
  ): () => void {
    return this.subscribe('video', videoId, callbacks);
  }

  public subscribeToCharacter(
    characterId: string,
    requestId: string,
    callbacks: {
      onProgress?: ProgressCallback;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: CompleteCallback;
      onError?: ErrorCallback;
    },
  ): () => void {
    return this.subscribe('character', characterId, callbacks, requestId);
  }

  public subscribeToVoice(
    voiceId: string,
    callbacks: {
      onProgress?: ProgressCallback;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: CompleteCallback;
      onError?: ErrorCallback;
    },
  ): () => void {
    return this.subscribe('voice', voiceId, callbacks);
  }

  private getSubscribeAction(resourceType: ProgressResourceType): string {
    if (resourceType === 'video') return 'subscribeToVideo';
    if (resourceType === 'character') return 'subscribeToCharacter';
    if (resourceType === 'voice') return 'subscribeToVoice';
    return 'subscribeToStory';
  }

  private getResourceKey(resourceType: ProgressResourceType): string {
    if (resourceType === 'character') return 'characterId';
    if (resourceType === 'voice') return 'voiceId';
    if (resourceType === 'video') return 'storyId';
    return 'storyId';
  }

  private sendSubscriptionMessage(subscription: Subscription): void {
    this.sendMessage({
      action: this.getSubscribeAction(subscription.resourceType),
      [this.getResourceKey(subscription.resourceType)]: subscription.resourceId,
      ...(subscription.requestId ? { requestId: subscription.requestId } : {}),
      userId: this.userId,
    });
  }

  private subscribe(
    resourceType: ProgressResourceType,
    resourceId: string,
    callbacks: {
      onProgress?: ProgressCallback;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: CompleteCallback;
      onError?: ErrorCallback;
    },
    requestId?: string,
  ): () => void {
    const key = `${resourceType}:${resourceId}`;
    const id = this.nextSubscriptionId++;
    const subscription: Subscription = {
      id,
      resourceType,
      resourceId,
      requestId,
      ...callbacks,
    };
    const subscriptionsForKey = this.subscriptions.get(key) || new Map<number, Subscription>();
    subscriptionsForKey.set(id, subscription);
    this.subscriptions.set(key, subscriptionsForKey);

    const latestProgress = this.latestProgress.get(key);
    if (latestProgress) {
      callbacks.onInitialProgress?.(latestProgress);
    }

    if (this.isAuthenticated) {
      this.sendSubscriptionMessage(subscription);
    }

    return () => {
      const current = this.subscriptions.get(key);
      if (!current?.has(id)) return;
      current.delete(id);
      if (current.size === 0) {
        this.subscriptions.delete(key);
        this.lastSequence.delete(key);
        this.messageBuffer.delete(key);
      }
    };
  }

  private resubscribeAll(): void {
    this.subscriptions.forEach((subscriptionsForKey) => {
      const subscription = subscriptionsForKey.values().next().value;
      if (subscription) {
        this.sendSubscriptionMessage(subscription);
      }
    });
  }

  private notifyProgressSnapshot(update: ProgressUpdate): void {
    const key = this.prepareProgressStream(update);

    if (update.sequence !== undefined) {
      const lastSeq = this.lastSequence.get(key) || 0;
      if (update.sequence < lastSeq) {
        return;
      }

      this.lastSequence.set(key, update.sequence);

      const buffer = this.messageBuffer.get(key);
      if (buffer) {
        const remaining = buffer.filter((buffered) => (buffered.sequence || 0) > (update.sequence || 0));
        if (remaining.length > 0) {
          this.messageBuffer.set(key, remaining);
        } else {
          this.messageBuffer.delete(key);
        }
      }
    }

    if (update.stage !== 'complete' && update.progress < 100) {
      this.latestProgress.set(key, update);
    } else {
      this.latestProgress.delete(key);
    }

    this.getSubscriptionsForKey(key).forEach((subscription) => subscription.onProgress?.(update));
    this.processBufferedMessages(key);
  }

  private notifySubscribers(update: ProgressUpdate): void {
    const key = this.prepareProgressStream(update);

    if (update.stage === 'complete' || update.progress >= 100) {
      this.notifyProgressSnapshot(update);
      return;
    }

    if (update.sequence === undefined) {
      this.notifyProgressSnapshot(update);
      return;
    }

    const lastSeq = this.lastSequence.get(key) || 0;
    const expectedSeq = lastSeq + 1;

    if (update.sequence <= lastSeq) {
      return;
    }

    if (lastSeq === 0 && update.sequence > expectedSeq) {
      this.processUpdate(key, update);
      this.processBufferedMessages(key);
      return;
    }

    if (update.sequence > expectedSeq) {
      // Reconnects and concurrent tabs can consume different queue messages.
      // A newer snapshot is still authoritative even when an earlier sequence
      // was delivered to another connection.
      this.processUpdate(key, update);
      this.processBufferedMessages(key);
      return;
    }

    this.processUpdate(key, update);
    this.processBufferedMessages(key);
  }

  private processUpdate(key: string, update: ProgressUpdate): void {
    this.lastSequence.set(key, update.sequence || 0);
    if (update.stage !== 'complete' && update.progress < 100) {
      this.latestProgress.set(key, update);
    } else {
      this.latestProgress.delete(key);
    }
    this.getSubscriptionsForKey(key).forEach((subscription) => subscription.onProgress?.(update));
  }

  private processBufferedMessages(key: string): void {
    const buffer = this.messageBuffer.get(key);
    if (!buffer?.length) {
      return;
    }

    let lastSeq = this.lastSequence.get(key) || 0;
    let processed = 0;

    for (const update of buffer) {
      if ((update.sequence || 0) !== lastSeq + 1) {
        break;
      }

      this.processUpdate(key, update);
      lastSeq = update.sequence || lastSeq;
      processed += 1;
    }

    if (processed > 0) {
      buffer.splice(0, processed);
      if (buffer.length === 0) {
        this.messageBuffer.delete(key);
      } else {
        this.messageBuffer.set(key, buffer);
      }
      this.processBufferedMessages(key);
    }
  }

  private prepareProgressStream(update: ProgressUpdate): string {
    const key = `${update.resourceType}:${update.resourceId}`;
    const operationId = update.metadata?.operationId;
    if (typeof operationId !== 'string' || !operationId) return key;

    const activeOperationId = this.activeOperationByResource.get(key);
    if (activeOperationId && activeOperationId !== operationId) {
      this.lastSequence.delete(key);
      this.messageBuffer.delete(key);
      this.latestProgress.delete(key);
    }
    this.activeOperationByResource.set(key, operationId);
    return key;
  }

  public onOpen(callback: () => void): void {
    this.onOpenCallbacks.push(callback);
  }

  public onClose(callback: () => void): void {
    this.onCloseCallbacks.push(callback);
  }

  public close(): void {
    this.isIntentionallyClosed = true;
    this.stopHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.subscriptions.clear();
    this.lastSequence.clear();
    this.messageBuffer.clear();
    this.latestProgress.clear();
    this.activeOperationByResource.clear();
    this.isAuthenticated = false;
    WebSocketManagerV2.instance = null;
  }
}

export default WebSocketManagerV2;
